import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormGroup, Input } from "@cofano/cck";
import { animated, useSpring } from "react-spring-new";
import z from "zod";
import NumberInput from "components/cck/NumberInput";
import { getStoredRegionLocale } from "utils/regionUtils";
import { createWidgetVisual } from "view/dashboard/widgets/visuals/types";
import { intl } from "LocaleWrapper";

export default createWidgetVisual({
	valueType: "number",
	label: intl.formatMessage({ id: "WIDGET_NUMBER" }),
	configSchema: z.object({
		prefix: z.string().default(""),
		suffix: z.string().default(""),
		decimals: z.number().int().min(0).max(4).default(0),
	}),

	render: ({ value, config }) => {
		const locale = getStoredRegionLocale();

		const format = useMemo(() => {
			const formatter = new Intl.NumberFormat(locale, {
				minimumFractionDigits: config.decimals,
				maximumFractionDigits: config.decimals,
			});
			return (n: number) => formatter.format(n);
		}, [ locale, config.decimals ]);

		const { number } = useSpring({
			from: { number: 0 },
			to: { number: value },
			config: { tension: 120, friction: 30, precision: 1 },
		});

		return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
			<span style={{ fontSize: 64 }}>
				{config.prefix}
				<animated.span style={{ fontVariantNumeric: "tabular-nums" }}>
					{number.to(format)}
				</animated.span>
				{config.suffix}
			</span>
		</div>;
	},

	form: ({ prefix }) => {
		const { control } = useFormContext();

		return <>
			<FormGroup label={intl.formatMessage({ id: "PREFIX" })} labelPlacement="left">
				<Controller
					name={`${prefix}.prefix`}
					control={control}
					render={({ field }) => (
						<Input {...field} value={(field.value as string) ?? ""} />
					)} />
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "SUFFIX" })} labelPlacement="left">
				<Controller
					name={`${prefix}.suffix`}
					control={control}
					render={({ field }) => (
						<Input {...field} value={(field.value as string) ?? ""} />
					)} />
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "DECIMALS" })} labelPlacement="left" required>
				<Controller
					name={`${prefix}.decimals`}
					control={control}
					render={({ field }) => (
						<NumberInput
							decimalScale={0}
							value={(field.value as number) ?? 0}
							onChange={entered => field.onChange(Math.min(4, Math.max(0, entered ?? 0)))} />
					)} />
			</FormGroup>
		</>;
	},
});
